const BODYLESS_METHODS = new Set<HttpMethod>(['GET', 'HEAD']);

const KNOWN_CONTENT_TYPES = [
  'application/json',
  'application/xml',
  'application/xhtml+xml',
  'application/pdf',
  'application/octet-stream',
  'text/plain',
  'text/html',
  'text/css',
  'text/csv',
  'text/javascript',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/svg+xml',
  'image/webp',
  'audio/mpeg',
  'audio/ogg',
  'audio/wav',
  'video/mp4',
  'video/webm',
  'video/ogg',
  'multipart/form-data',
] as const;

type KnownContentType = (typeof KNOWN_CONTENT_TYPES)[number];

type RequestPriorityValue = 'high' | 'low' | 'auto';
type RequestDuplexValue = 'half';

type SearchParamValue = string | number | boolean | null | undefined;
type SearchParamsInput =
  | URLSearchParams
  | ReadonlyArray<[string, SearchParamValue]>
  | Record<string, SearchParamValue>;

type JsonReplacerFn = (this: any, key: string, value: any) => any;

export interface JsonBodyOptions {
  /**
   * A replacer function mirroring JSON.stringify's second argument.
   */
  replacer?: JsonReplacerFn | null;
  /**
   * Property names to include (mirrors JSON.stringify's array signature).
   */
  propertyList?: Array<number | string>;
  /**
   * Indentation parameter forwarded to JSON.stringify.
   */
  space?: number | string;
}

export type ContentType =
  | KnownContentType
  | (string & { readonly __contentTypeBrand?: never });

export type HttpMethod =
  | 'GET'
  | 'POST'
  | 'PUT'
  | 'DELETE'
  | 'HEAD'
  | 'OPTIONS'
  | 'CONNECT'
  | 'TRACE'
  | 'PATCH';

export type EnhancedRequestInit = Omit<RequestInit, 'headers'> & {
  headers?: HeadersInit;
  priority?: RequestPriorityValue;
  duplex?: RequestDuplexValue;
  browsingTopics?: boolean;
};

export type FetchOptions = EnhancedRequestInit;

const toHeadersRecord = (headers: Headers): Record<string, string> => {
  const record: Record<string, string> = {};
  headers.forEach((value, key) => {
    record[key] = value;
  });

  return record;
};

const normalizeSearchParams = (params: SearchParamsInput): URLSearchParams => {
  if (params instanceof URLSearchParams) {
    return params;
  }

  const searchParams = new URLSearchParams();

  const entries = Array.isArray(params) ? params : Object.entries(params ?? {});

  for (const [key, value] of entries) {
    if (value === undefined || value === null) continue;
    searchParams.append(key, String(value));
  }

  return searchParams;
};

/**
 * FetchOptionsBuilder provides a fluent, type-safe way to construct RequestInit
 * objects that align with the latest Fetch API capabilities described on MDN.
 */
export class FetchOptionsBuilder {
  private readonly headers: Headers;
  private readonly options: EnhancedRequestInit;

  constructor(initial?: EnhancedRequestInit) {
    const { headers: initialHeaders, ...rest } = initial ?? {};
    this.headers = new Headers(initialHeaders ?? undefined);
    this.options = { ...rest };
  }

  /**
   * Create a builder seeded with an existing RequestInit-like object.
   */
  static from(initial?: EnhancedRequestInit) {
    return new FetchOptionsBuilder(initial);
  }

  /**
   * Clone the builder and its current state.
   */
  clone() {
    return new FetchOptionsBuilder(this.build());
  }

  /**
   * Merge a RequestInit-like object into the builder.
   */
  merge(init: EnhancedRequestInit) {
    const { headers, ...rest } = init;
    Object.assign(this.options, rest);

    if (headers) {
      new Headers(headers).forEach((value, key) =>
        this.headers.set(key, value)
      );
    }

    return this;
  }

  /**
   * Set (or remove) an individual header.
   */
  header(key: string, value: string | null) {
    if (value === null) {
      this.headers.delete(key);
    } else {
      this.headers.set(key, value);
    }

    return this;
  }

  /**
   * Merge multiple headers at once.
   */
  mergeHeaders(headers: HeadersInit) {
    new Headers(headers).forEach((value, key) => this.headers.set(key, value));

    return this;
  }

  /**
   * Set a Bearer token on the Authorization header.
   */
  bearerToken(token: string | number) {
    this.headers.set('Authorization', `Bearer ${token}`);

    return this;
  }

  /**
   * Set the HTTP method.
   */
  method(method: HttpMethod) {
    this.options.method = method;

    return this;
  }

  /**
   * Set the Fetch credentials mode.
   */
  credentials(credentials: RequestCredentials) {
    this.options.credentials = credentials;

    return this;
  }

  /**
   * Configure cache strategy per the Fetch spec.
   */
  cache(cache: RequestCache) {
    this.options.cache = cache;

    return this;
  }

  /**
   * Configure redirect handling.
   */
  redirect(redirect: RequestRedirect) {
    this.options.redirect = redirect;

    return this;
  }

  /**
   * Set the request referrer string.
   */
  referrer(referrer: string) {
    this.options.referrer = referrer;

    return this;
  }

  /**
   * Set the referrer policy.
   */
  referrerPolicy(policy: ReferrerPolicy) {
    this.options.referrerPolicy = policy;

    return this;
  }

  /**
   * Configure the request mode.
   */
  mode(mode: RequestMode) {
    this.options.mode = mode;

    return this;
  }

  /**
   * Attach an AbortSignal for cancellation.
   */
  signal(signal: AbortSignal) {
    this.options.signal = signal;

    return this;
  }

  /**
   * Enable or disable keepalive on the request.
   */
  keepalive(enable = true) {
    this.options.keepalive = enable;

    return this;
  }

  /**
   * Configure Fetch priority, supported by modern browsers (Chrome 115+ per MDN).
   */
  priority(priority: RequestPriorityValue) {
    this.options.priority = priority;

    return this;
  }

  /**
   * Toggle the experimental browsingTopics flag.
   */
  browsingTopics(enable = true) {
    this.options.browsingTopics = enable;

    return this;
  }

  /**
   * Set the duplex mode required for streaming uploads.
   */
  duplex(duplex: RequestDuplexValue) {
    this.options.duplex = duplex;

    return this;
  }

  /**
   * Apply an integrity hash (SRI).
   */
  integrity(hash: string) {
    this.options.integrity = hash;

    return this;
  }

  /**
   * Set the Content-Type header.
   */
  contentType(contentType: ContentType) {
    this.headers.set('Content-Type', contentType);

    return this;
  }

  /**
   * Set the Accept header.
   */
  accept(...mimeTypes: string[]) {
    this.headers.set('Accept', mimeTypes.join(', '));

    return this;
  }

  /**
   * Set a strongly typed request body.
   */
  body(body: BodyInit | null) {
    this.ensureBodyAllowed();
    this.options.body = body ?? undefined;

    return this;
  }

  /**
   * Serialize JSON and set the appropriate headers.
   */
  json<T>(payload: T, options?: JsonBodyOptions) {
    let serialized: string;

    if (options?.propertyList !== undefined) {
      serialized = JSON.stringify(
        payload,
        options.propertyList,
        options?.space
      );
    } else if (options?.replacer) {
      serialized = JSON.stringify(payload, options.replacer, options?.space);
    } else {
      serialized = JSON.stringify(payload, undefined, options?.space);
    }
    this.contentType('application/json');

    return this.body(serialized);
  }

  /**
   * Attach URLSearchParams or a plain object as the request body.
   */
  searchParams(params: SearchParamsInput) {
    const normalized = normalizeSearchParams(params);
    this.contentType('application/x-www-form-urlencoded');

    return this.body(normalized);
  }

  /**
   * Append FormData without overriding the automatically generated boundary header.
   */
  multipart(formData: FormData) {
    return this.body(formData);
  }

  /**
   * Set headers for classic URL-encoded forms.
   */
  formUrlEncoded(values: Record<string, SearchParamValue>) {
    const normalized = normalizeSearchParams(values);
    this.contentType('application/x-www-form-urlencoded');

    return this.body(normalized);
  }

  /**
   * Build the final RequestInit object.
   */
  build(): FetchOptions {
    const headersRecord = toHeadersRecord(this.headers);
    const hasHeaders = Object.keys(headersRecord).length > 0;

    return {
      ...this.options,
      headers: hasHeaders ? headersRecord : undefined,
    };
  }

  private ensureBodyAllowed() {
    const method = this.options.method as HttpMethod | undefined;

    if (!method) {
      this.options.method = 'POST';
      return;
    }

    if (BODYLESS_METHODS.has(method)) {
      throw new Error(
        `Cannot attach a body to ${method} requests. Set a different method before calling body().`
      );
    }
  }
}
